<script lang="ts">
    import { systemStatus, type SystemStatus } from '../stores/status';
    import StatusDot from '../components/StatusDot.svelte';
    import Button from '../components/forms/Button.svelte';
    import { addToast, updateToast } from '../stores/toast';

    const JOB_DURATION = 30_000;

    const states: { value: SystemStatus; label: string; description: string }[] = [
        { value: 'ok',      label: 'OK',      description: 'System is connected and operating normally.' },
        { value: 'warning', label: 'Warning', description: 'System is reachable but reporting issues.' },
        { value: 'error',   label: 'Error',   description: 'System is unreachable or has critical errors.' },
    ];

    let jobRunning = false;
    let elapsed = 0;
    let elapsedTimer: ReturnType<typeof setInterval>;

    function startJob() {
        if (jobRunning) return;
        jobRunning = true;
        elapsed = 0;

        const id = addToast('Configuration sync in progress…', 'info', { title: 'Background Job', duration: 0, progress: 'indeterminate', dismissible: false });

        elapsedTimer = setInterval(() => { elapsed += 1; }, 1000);

        setTimeout(() => {
            clearInterval(elapsedTimer);
            updateToast(id, {
                type:        'success',
                title:       'Job Complete',
                message:     'Configuration synced successfully.',
                duration:    5000,
                progress:    undefined,
                dismissible: true,
            });
            jobRunning = false;
        }, JOB_DURATION);
    }
</script>

<div class="page">
    <h1>Status Test</h1>

    <section class="card">
        <h2>Current state</h2>
        <div class="state-display">
            <StatusDot status={$systemStatus} large />
            <div>
                <strong class="state-name state-name--{$systemStatus}">{$systemStatus.toUpperCase()}</strong>
                <p class="state-desc">
                    {states.find(s => s.value === $systemStatus)?.description}
                </p>
            </div>
        </div>
    </section>

    <section class="card">
        <h2>Toggle system status</h2>
        <div class="status-cards">
            {#each states as state}
                <button
                    class="status-card status-card--{state.value}"
                    class:active={$systemStatus === state.value}
                    on:click={() => systemStatus.set(state.value)}
                    aria-pressed={$systemStatus === state.value}
                >
                    <span class="card-label">
                        {state.label}
                        <StatusDot status={state.value} />
                    </span>
                    <span class="card-desc">{state.description}</span>
                </button>
            {/each}
        </div>
    </section>    <section class="card">
        <h2>Background Job</h2>
        <div class="card-body">
            <p class="section-desc">Simulates a 30-second background job. A sticky toast tracks its progress and auto-dismisses once complete.</p>
            <div class="job-row">
                <Button variant="primary" disabled={jobRunning} on:click={startJob}>
                    {jobRunning ? 'Running…' : 'Start Config Sync'}
                </Button>
                {#if jobRunning}
                    <span class="job-elapsed">{elapsed}s / 30s</span>
                {/if}
            </div>
        </div>
    </section>

</div>

<style lang="scss">
    // ── Status cards ──────────────────────────────────────────────────────────

    .status-cards {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.75em;
    }

    .status-card {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.4em;
        padding: 1.1em 1.25em;
        background-color: $color-sidebar-bg;
        border: 2px solid $color-header-border;
        border-radius: 8px;
        cursor: pointer;
        text-align: left;
        font-family: inherit;
        transition:
            border-color 0.15s ease,
            background-color 0.15s ease,
            transform 0.1s ease;

        &:hover { transform: translateY(-1px); }
        &:active { transform: translateY(0); }

        &.active {
            background-color: $color-bg-active;

            &.status-card--ok      { border-color: $color-status-ok; }
            &.status-card--warning { border-color: $color-status-warning; }
            &.status-card--error   { border-color: $color-status-error; }
        }
    }

    .card-label {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        font-size: 1em;
        font-weight: 500;
        color: $color-text;
    }

    .card-desc {
        font-size: 0.8em;
        color: $color-disabled-text;
        line-height: 1.4;
    }

    // ── Current state display ─────────────────────────────────────────────────

    .state-display {
        display: flex;
        align-items: center;
        gap: 1em;

        p { margin: 0.2em 0 0; font-size: 0.9em; color: $color-disabled-text; }
    }

    .state-name {
        font-size: 1.05em;
        font-weight: 600;
        letter-spacing: 0.05em;

        &--ok      { color: $color-status-ok; }
        &--warning { color: $color-status-warning; }
        &--error   { color: $color-status-error; }
    }

    .section-desc {
        color: $color-text-secondary;
        font-size: 0.875em;
    }

    .job-row {
        display: flex;
        align-items: center;
        gap: 1em;
    }

    .job-elapsed {
        font-size: 0.875em;
        color: $color-text-muted;
        font-variant-numeric: tabular-nums;
    }
</style>
